import { Progress as ProgressPrimitive } from "@base-ui/react/progress";
import type React from "react";
import { cn } from "@/lib/utils";

export const ProgressTrack = ({
  className,
  ...props
}: ProgressPrimitive.Track.Props): React.ReactElement => (
  <ProgressPrimitive.Track
    className={cn("block h-1.5 w-full overflow-hidden rounded-full bg-input", className)}
    data-slot="progress-track"
    {...props}
  />
);

export const ProgressIndicator = ({
  className,
  ...props
}: ProgressPrimitive.Indicator.Props): React.ReactElement => (
  <ProgressPrimitive.Indicator
    // Base UI drives the indicator's `width` alone, so name it: `all` would
    // also animate the inherited height and any colour a caller overrides.
    // Reduced motion is handled once, globally, in app.css.
    className={cn("bg-primary transition-[width] duration-500", className)}
    data-slot="progress-indicator"
    {...props}
  />
);

export const Progress = ({
  className,
  children,
  ...props
}: ProgressPrimitive.Root.Props): React.ReactElement => {
  const hasChildren = Boolean(children);

  return (
    <ProgressPrimitive.Root
      className={cn("flex w-full flex-col gap-2", className)}
      data-slot="progress"
      {...props}
    >
      {hasChildren ? (
        children
      ) : (
        <ProgressTrack>
          <ProgressIndicator />
        </ProgressTrack>
      )}
    </ProgressPrimitive.Root>
  );
};

export const ProgressLabel = ({
  className,
  ...props
}: ProgressPrimitive.Label.Props): React.ReactElement => (
  <ProgressPrimitive.Label
    className={cn("font-medium text-sm", className)}
    data-slot="progress-label"
    {...props}
  />
);

export const ProgressValue = ({
  className,
  ...props
}: ProgressPrimitive.Value.Props): React.ReactElement => (
  <ProgressPrimitive.Value
    className={cn("text-sm tabular-nums", className)}
    data-slot="progress-value"
    {...props}
  />
);

export { ProgressPrimitive };
